import { useEffect, useMemo, useState } from 'react';

import PaginationSection from '../../../common/PaginationSection';
import {
  adminPageUrl,
  marketColumn,
  orderDateColumn,
  orderNoColumn,
  orderStatus,
  productNameColumn,
  productNoColumn,
  productOptionColumn,
  quantityColumn,
  receiverColumn,
  recevierMemo,
  salesAmountColumn,
} from '../orderTableColumnBlocks';

import EmptyFaceIcon from '@/assets/icons/EmptyFaceIcon.svg?react';
import { TooltipProvider } from '@/components/atoms/PcTooltip';
import { LayoutOrderColumn } from '@/pages/orders/_layouts/LayoutOrderColumn';
import { OrderItem } from '@/types/order-list';
import { Stores } from '@/types/stores';
import MarketTable from '../../../common/MarketTable';
import { useOrderDetails } from '../../OrderDetailsProvider';
import { DEFAULT_PAGE_SIZE } from '@/constants/order';

interface Props {
  page: number;
  onChangePage: (page: number) => void;
  totalPages: number;
  orderList: OrderItem[];
  marketList: Stores[];
}

export function OrderTableColumn({ page, onChangePage, totalPages, orderList, marketList }: Props) {
  const { checkedOrders, setCheckedOrders, shippingCompanyData, shippingInfo, setShippingInfo } =
    useOrderDetails();

  useEffect(() => {
    setCheckedOrders([]);
  }, [orderList.length]);

  const handleCheck = (OrderItemNo: string, checked: boolean) => {
    setCheckedOrders((prev) =>
      checked
        ? [...prev, orderList.find((item) => item.orderItemNo === OrderItemNo)!]
        : prev.filter((item) => item.orderItemNo !== OrderItemNo),
    );
  };

  // 마켓 이름 또는 StoreId 등으로 매칭 (실제 데이터 구조에 맞게 조정)
  const getMarketIcon = (order: OrderItem) => {
    // 예시: StoreId로 매칭
    const market = marketList.find(
      (m) => m.marketId === order.marketId && m.storeId === order.storeId, // 필요시 StoreId도 비교
    );
    return market?.marketIconUrl || '';
  };

  const columns = useMemo(() => {
    const baseColumns = [
      orderNoColumn,
      orderStatus,
      marketColumn,
      orderDateColumn,
      productNameColumn,
      productOptionColumn,
      // carrierColumn은 shippingCompanyData가 비어있지 않을 때만 추가
      salesAmountColumn,
      quantityColumn,
      productNoColumn({ showSellerPageButton: true }),
      adminPageUrl,
      receiverColumn,
      recevierMemo,
    ];

    return baseColumns;
  }, [
    // allChecked,
    // handleCheckAll,
    checkedOrders,
    handleCheck,
    getMarketIcon,
    shippingCompanyData,
    shippingInfo,
    setShippingInfo,
  ]);

  // columnOrder는 'checkbox'를 제외한 나머지 컬럼 id만 포함
  const [columnOrder, setColumnOrder] = useState(() =>
    columns.filter((c) => c.id !== 'checkbox').map((c) => c.id!),
  );

  return (
    <TooltipProvider>
      <LayoutOrderColumn className="relative">
        {/* MarketTable을 감싸는 div 추가 */}
        {/* <div className="min-h-0 overflow-x-auto overflow-y-scroll"> */}
        {/* <div> */}
        {/* ✅ 남은 공간 모두 차지 + 스크롤 */}
        <MarketTable
          orderList={orderList}
          columns={columns}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
          // checkedOrders={checkedOrders}
          // setCheckedOrders={setCheckedOrders}
          pageIndex={page}
          pageSize={DEFAULT_PAGE_SIZE}
          pageCount={Math.ceil(orderList.length / DEFAULT_PAGE_SIZE)} // 클라이언트사이드 시
        />
        {orderList.length === 0 && (
          <div className="absolute inset-0 top-[40px] z-[20] flex flex-col items-center justify-center gap-[20px] rounded-[20px] bg-white">
            <EmptyFaceIcon />
            <p className="text-gr-500">검색결과가 없어요.</p>
          </div>
        )}
        {/* </div> */}
      </LayoutOrderColumn>

      {/* 페이지네이션 컨트롤 */}
      <div className="flex h-[70px] max-h-[70px] items-center justify-center gap-4 border-t">
        <PaginationSection page={page} onChangePage={onChangePage} totalPages={totalPages} />
      </div>
    </TooltipProvider>
  );
}

export default OrderTableColumn;
